import { useMemo } from 'react';

import {
  findNearestWorkDay,
  formatScheduleForMedcenter,
  formatWorkingDaysForModal,
  getDaysFromToday,
} from '../../utils/scheduleUtils';

import type { ISchedule, IBookingDoctorData } from '@/entities/booking/model/types';
import type { IScheduleInMedcenter } from '@/entities/medcenter/model/types';

interface UseBookingDataProps {
  bookingData: IBookingDoctorData[] | null;
  doctorId: number | null;
  medcenterId?: number | null;
}

interface UseBookingDataReturn {
  bookingDataItem: IBookingDoctorData | null;
  scheduleData: ISchedule[];
  nearestWorkDay: ISchedule | null;
  formattedSchedule: IScheduleInMedcenter[];
  shouldShowLimitVisualItems: boolean;
  workingDaysCount: number;
}

/**
 * Хук для получения и форматирования данных о бронировании врача.
 *
 * @param {Object} params - Параметры хука.
 * @param {IBookingDoctorData[] | null} params.bookingData - Массив данных о бронировании врача.
 * @param {number | null} params.doctorId - Идентификатор врача.
 * @param {number | null | undefined} [params.medcenterId] - Необязательный идентификатор медцентра.
 */
export const useBookingData = ({ bookingData, doctorId, medcenterId }: UseBookingDataProps): UseBookingDataReturn => {
  const bookingDataItem = useMemo(() => {
    if (!bookingData || bookingData.length === 0 || !doctorId) return null;

    return medcenterId ? bookingData.find(data => data.medcenterId === medcenterId) || bookingData[0] : bookingData[0];
  }, [bookingData, doctorId, medcenterId]);

  const scheduleData: ISchedule[] = useMemo(() => {
    return bookingDataItem?.onlineBooking?.schedule || [];
  }, [bookingDataItem]);

  const nearestWorkDay = useMemo(() => {
    return findNearestWorkDay(scheduleData);
  }, [scheduleData]);

  const workingDaysCount = useMemo(() => {
    return scheduleData.filter(
      day => day.isWork && day.timeslots && day.timeslots.length > 0 && getDaysFromToday(day.date) >= 0,
    ).length;
  }, [scheduleData]);

  const { formattedSchedule, shouldShowLimitVisualItems } = useMemo(
    () => ({
      formattedSchedule:
        workingDaysCount >= 3 ? formatWorkingDaysForModal(scheduleData) : formatScheduleForMedcenter(scheduleData),
      shouldShowLimitVisualItems: workingDaysCount < 3,
    }),
    [scheduleData, workingDaysCount],
  );

  return {
    bookingDataItem,
    scheduleData,
    nearestWorkDay,
    formattedSchedule,
    shouldShowLimitVisualItems,
    workingDaysCount,
  };
};
